const RE = /^(\d{4})-(\d{2})-(\d{2})$/;
export const isISODate = (s) => {
    if (typeof s !== 'string' || !RE.test(s))
        return false;
    const t = toUTC(s).getTime();
    return !Number.isNaN(t) && fromUTC(new Date(t)) === s;
};
export const toUTC = (d) => {
    const m = RE.exec(d);
    if (!m)
        throw new TypeError(`Bad ISODate: ${d}`);
    return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
};
export const fromUTC = (dt) => dt.toISOString().slice(0, 10);
export const todayISO = (now = new Date()) => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
/** Main time zone per country (one zone each, so Perth follows Sydney). */
export const COUNTRY_ZONES = {
    AU: 'Australia/Sydney', GB: 'Europe/London', US: 'America/New_York', NZ: 'Pacific/Auckland', CA: 'America/Toronto', IE: 'Europe/Dublin',
    DE: 'Europe/Berlin', FR: 'Europe/Paris', SG: 'Asia/Singapore', JP: 'Asia/Tokyo', ZA: 'Africa/Johannesburg',
};
/** Today's date in a country's main time zone (UTC for other countries), whatever zone the machine's clock is in. */
export const todayInCountry = (country, now = new Date()) => new Intl.DateTimeFormat('en-CA', { timeZone: COUNTRY_ZONES[country] ?? 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
export const nowISO = () => new Date().toISOString();
export const parts = (d) => {
    const m = RE.exec(d);
    if (!m)
        throw new TypeError(`Bad ISODate: ${d}`);
    return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
};
export const mkDate = (y, m, d) => fromUTC(new Date(Date.UTC(y, m - 1, d)));
export const daysInMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
export const isLeapYear = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
export const addDays = (d, n) => { const t = toUTC(d); t.setUTCDate(t.getUTCDate() + n); return fromUTC(t); };
/** Adds months, clamping the day to the end of the target month (31 Jan + 1 → 28/29 Feb). */
export const addMonths = (d, n) => {
    const p = parts(d);
    const total = p.y * 12 + (p.m - 1) + n;
    const y = Math.floor(total / 12), m = (total % 12) + 1;
    return mkDate(y, m, Math.min(p.d, daysInMonth(y, m)));
};
export const addYears = (d, n) => addMonths(d, n * 12);
export const diffDays = (a, b) => Math.round((toUTC(b).getTime() - toUTC(a).getTime()) / 86_400_000);
export const cmpDate = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
export const minDate = (a, b) => (a <= b ? a : b);
export const maxDate = (a, b) => (a >= b ? a : b);
export const startOfMonth = (d) => { const p = parts(d); return mkDate(p.y, p.m, 1); };
export const endOfMonth = (d) => { const p = parts(d); return mkDate(p.y, p.m, daysInMonth(p.y, p.m)); };
/** 0 = Sunday … 6 = Saturday */
export const dayOfWeek = (d) => toUTC(d).getUTCDay();
export const isWeekend = (d) => { const w = dayOfWeek(d); return w === 0 || w === 6; };
/**
 * Financial year containing `d`, for a FY starting on `startMonth`/`startDay` (AU: 7/1, UK: 4/6, US: 1/1, NZ: 4/1).
 * Label is e.g. "2025-26" (AU style) or "2025" when the FY is a calendar year.
 */
export const financialYearOf = (d, startMonth = 7, startDay = 1) => {
    const p = parts(d);
    const startsThisYear = p.m > startMonth || (p.m === startMonth && p.d >= startDay);
    const startYear = startsThisYear ? p.y : p.y - 1;
    const start = mkDate(startYear, startMonth, startDay);
    const end = addDays(addYears(start, 1), -1);
    const endYear = startYear + (startMonth === 1 && startDay === 1 ? 0 : 1);
    const label = startMonth === 1 && startDay === 1 ? String(startYear) : `${startYear}-${String(endYear).slice(2)}`;
    return { label, start, end, endYear };
};
/** Enumerate FYs from a to b inclusive. */
export const financialYearsBetween = (a, b, startMonth = 7, startDay = 1) => {
    const out = [];
    let fy = financialYearOf(a, startMonth, startDay);
    while (fy.start <= b) {
        out.push(fy);
        fy = financialYearOf(addDays(fy.end, 1), startMonth, startDay);
    }
    return out;
};
/** Inclusive date range iterator. */
export function* eachDay(a, b) { for (let d = a; d <= b; d = addDays(d, 1))
    yield d; }
//# sourceMappingURL=dates.js.map